import { Form, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import BrandSettingsLayout from "@/components/brand/settings/brand-settings-layout"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { brandSettingsRewardsPath } from "@/routes"

/**
 * OKS-456 — Brand Settings → Rewards page.
 *
 * Edits the brand-level reward DEFAULTS every mission inherits (per-
 * mission overrides live on the mission form). Two grouped subsections:
 *   XP    — base / on-time bonus / engagement bonus
 *   coins — base / on-time bonus            (no coin engagement bonus)
 *
 * Built on the Thresholds precedent: Inertia `<Form>` + plain shadcn
 * `Input`/`Label` (NO shadcn Form / RHF / zod — Crew rule); errors
 * render from the render-prop `errors` object via `<InputError>`.
 * Out-of-range values are rejected server-side via `ArgumentError` from
 * the model writer and surfaced inline. Agency users see a read-only
 * page (`can_edit` false → no Save button).
 */

interface RangeBound {
  min: number
  max: number
}

export interface BrandSettingsRewardsProps {
  brand: {
    id: number
    xp_base_per_mission: number
    xp_on_time_bonus: number
    xp_engagement_bonus: number
    coins_base_per_mission: number
    coins_on_time_bonus: number
  }
  ranges: {
    xp_base_per_mission: RangeBound
    xp_on_time_bonus: RangeBound
    xp_engagement_bonus: RangeBound
    coins_base_per_mission: RangeBound
    coins_on_time_bonus: RangeBound
  }
  can_edit: boolean
}

type RewardKey = keyof BrandSettingsRewardsProps["brand"]

export default function BrandSettingsRewards({
  brand,
  ranges,
  can_edit,
}: BrandSettingsRewardsProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("settings.brand.rewards.title")} />
      <BrandSettingsLayout>
        <div data-testid="brand-settings-rewards" className="space-y-6">
          <HeadingSmall
            title={t("settings.brand.rewards.title")}
            description={t("settings.brand.rewards.description")}
          />

          <Form
            method="patch"
            action={brandSettingsRewardsPath()}
            options={{ preserveScroll: true }}
            className="space-y-8"
            data-testid="brand-settings-rewards-form"
          >
            {({ errors, processing }) => {
              const renderField = (
                key: Exclude<RewardKey, "id">,
                extraHelper?: string,
              ) => {
                const fieldId = `reward-${key.replace(/_/g, "-")}`
                const helperId = `${fieldId}-helper`
                return (
                  <div className="grid gap-2" key={key}>
                    <Label htmlFor={fieldId}>
                      {t(`settings.brand.rewards.fields.${key}.label`)}
                    </Label>
                    <Input
                      id={fieldId}
                      type="number"
                      name={`brand[${key}]`}
                      min={ranges[key].min}
                      step={1}
                      inputMode="numeric"
                      defaultValue={brand[key]}
                      readOnly={!can_edit}
                      aria-readonly={!can_edit}
                      aria-describedby={helperId}
                      aria-invalid={errors[key] ? true : undefined}
                      data-testid={fieldId}
                      className="max-w-[180px]"
                    />
                    <p id={helperId} className="text-muted-foreground text-sm">
                      {t(`settings.brand.rewards.fields.${key}.helper`)}
                      {extraHelper ? ` ${extraHelper}` : null}
                    </p>
                    <InputError messages={errors[key]} />
                  </div>
                )
              }

              return (
                <>
                  <section className="space-y-4">
                    <h2 className="text-sm font-medium">
                      {t("settings.brand.rewards.xp_legend")}
                    </h2>
                    {renderField("xp_base_per_mission")}
                    {renderField("xp_on_time_bonus")}
                    {renderField(
                      "xp_engagement_bonus",
                      t("settings.brand.rewards.engagement_note"),
                    )}
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-sm font-medium">
                      {t("settings.brand.rewards.coins_legend")}
                    </h2>
                    {renderField("coins_base_per_mission")}
                    {renderField("coins_on_time_bonus")}
                  </section>

                  {can_edit ? (
                    <Button
                      type="submit"
                      disabled={processing}
                      data-testid="brand-settings-rewards-save"
                    >
                      {processing
                        ? t("settings.brand.rewards.saving")
                        : t("settings.brand.rewards.save")}
                    </Button>
                  ) : (
                    <p
                      role="status"
                      data-testid="brand-settings-rewards-readonly"
                      className="text-muted-foreground text-sm"
                    >
                      {t("settings.brand.rewards.read_only_hint")}
                    </p>
                  )}
                </>
              )
            }}
          </Form>
        </div>
      </BrandSettingsLayout>
    </AppLayout>
  )
}
